import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Activity, Loader2, Mail, KeyRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import AdminChoiceModal, { getAdminPref } from "@/components/AdminChoiceModal";
import { api, setToken } from "@/lib/api";

// ---------- OTP tab ---------------------------------------------------------
function OtpLoginForm({ onSuccess, initialEmail, setEmail }) {
  const [step, setStep] = useState("email");   // "email" | "code"
  const [localEmail, setLocalEmail] = useState(initialEmail || "");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [devCode, setDevCode] = useState(null);   // shown only when SendGrid unconfigured

  const requestCode = async () => {
    if (!localEmail || !localEmail.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/auth/otp/request", { email: localEmail.trim() });
      setStep("code");
      setEmail(localEmail);
      setCooldown(r.data.cooldown_seconds || 60);
      const tick = setInterval(() => {
        setCooldown((c) => {
          if (c <= 1) { clearInterval(tick); return 0; }
          return c - 1;
        });
      }, 1000);
      if (r.data.dev_mode_code) {
        setDevCode(r.data.dev_mode_code);
        toast.warning("SendGrid not configured — dev code shown below");
      } else {
        toast.success(r.data.message || "Code sent if the email is registered.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to request code");
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e?.preventDefault?.();
    if (code.length !== 6) { toast.error("Enter the 6-digit code"); return; }
    setBusy(true);
    try {
      const r = await api.post("/auth/otp/verify", { email: localEmail.trim(), code });
      setToken(r.data.token);
      onSuccess(r.data.user);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  if (step === "email") {
    return (
      <div className="space-y-4" data-testid="otp-step-email">
        <div>
          <label className="vx-label block mb-1.5">Email</label>
          <input type="email" required value={localEmail} onChange={(e) => setLocalEmail(e.target.value)}
                 className="vx-input" data-testid="otp-email-input" autoComplete="email" />
        </div>
        <button
          type="button"
          onClick={requestCode}
          disabled={busy || !localEmail}
          data-testid="otp-send-btn"
          className="w-full h-11 rounded-md bg-[#3b82f6] hover:bg-[#2563eb] font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
          Send code
        </button>
        <div className="text-[11px] text-[#6b7280] font-mono leading-relaxed">
          We&apos;ll email you a 6-digit code that expires in 5 minutes.
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={verify} className="space-y-4" data-testid="otp-step-code">
      <div>
        <label className="vx-label block mb-1.5">
          Code sent to <span className="text-[#e5e7eb]">{localEmail}</span>
        </label>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={6}
          autoFocus
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          placeholder="123456"
          className="vx-input font-mono text-2xl text-center tracking-[0.6em]"
          data-testid="otp-code-input"
        />
        {devCode && (
          <div className="mt-2 p-2 rounded bg-[#fbbf24]/10 border border-[#fbbf24]/40 text-[11px] font-mono text-[#fbbf24]" data-testid="otp-dev-code">
            DEV code (SendGrid unconfigured): <b>{devCode}</b>
          </div>
        )}
      </div>
      <button
        type="submit"
        disabled={busy || code.length !== 6}
        data-testid="otp-verify-btn"
        className="w-full h-11 rounded-md bg-[#10b981] hover:bg-[#059669] text-black font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
        Verify & sign in
      </button>
      <div className="flex items-center justify-between text-[11px] font-mono">
        <button
          type="button"
          onClick={() => { setStep("email"); setCode(""); setDevCode(null); }}
          className="text-[#6b7280] hover:text-[#9ca3af]"
          data-testid="otp-change-email-btn"
        >
          ← Change email
        </button>
        <button
          type="button"
          disabled={cooldown > 0 || busy}
          onClick={requestCode}
          className="text-[#3b82f6] hover:underline disabled:text-[#6b7280] disabled:no-underline disabled:cursor-not-allowed"
          data-testid="otp-resend-btn"
        >
          {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
        </button>
      </div>
    </form>
  );
}

export default function Login() {
  const { login, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showAdminChoice, setShowAdminChoice] = useState(false);
  const [mode, setMode] = useState("otp");         // "password" | "otp" — OTP is default for clients
  const [unverified, setUnverified] = useState(null);   // {email} when login blocked
  const [resendingUnverified, setResendingUnverified] = useState(false);

  const handleUnverifiedResend = async () => {
    setResendingUnverified(true);
    try {
      const r = await api.post("/auth/resend-verification", { email: unverified.email });
      toast.success(r.data.message || "Sent");
      if (r.data.dev_mode_verify_url) {
        toast.info("Dev URL: " + r.data.dev_mode_verify_url, { duration: 15000 });
      }
    } catch (e) {
      toast.error("Failed to resend");
    } finally { setResendingUnverified(false); }
  };

  const handleRoleRedirect = (user) => {
    if (user?.role === "admin") {
      const pref = getAdminPref();
      if (pref === "admin") navigate("/admin", { replace: true });
      else if (pref === "user") navigate("/dashboard", { replace: true });
      else {
        sessionStorage.setItem("veerox_admin_choice_pending", "1");
        setShowAdminChoice(true);
      }
    } else {
      const dest = location.state?.from?.pathname || "/dashboard";
      navigate(dest, { replace: true });
    }
  };

  // OTP path stores its own token via setToken; AuthContext still needs to
  // learn about the fresh user. Trigger a re-hydration then redirect.
  const onLoginSuccess = async (user) => {
    toast.success("Welcome back");
    // Refresh AuthContext by hitting /auth/me (already writes to storage on success)
    try { await refreshUser?.(); } catch (_) { /* non-fatal */ }
    handleRoleRedirect(user);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setUnverified(null);
    try {
      const user = await login(email.trim(), password);
      toast.success("Welcome back");
      handleRoleRedirect(user);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Phase 8C — email_not_verified case surfaces the resend UI
      if (detail && typeof detail === "object" && detail.code === "email_not_verified") {
        setUnverified({ email: detail.email || email.trim() });
        toast.warning("Please verify your email first");
      } else {
        toast.error(detail || "Login failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#0a0e1a]">
      {/* Left panel — brand */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 p-12 border-r border-[#1f2937] vx-grid-bg">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-[#3b82f6]/10 border border-[#3b82f6]/40 flex items-center justify-center">
            <Activity className="w-5 h-5 text-[#3b82f6]" strokeWidth={2} />
          </div>
          <div>
            <div className="font-heading font-black text-2xl tracking-tighter leading-none">VEEROX</div>
            <div className="vx-label mt-1">Autonomous Trading Intelligence</div>
          </div>
        </div>
        <div>
          <h1 className="font-heading text-4xl xl:text-5xl font-black tracking-tighter leading-[1.05] mb-6">
            The Trading OS that<br/>
            <span className="text-[#3b82f6]">thinks before it trades.</span>
          </h1>
          <p className="text-[#9ca3af] max-w-md leading-relaxed">
            Not another Expert Advisor. A 9-pillar intelligence platform that reads market regime,
            picks the right strategy, and gates every trade through a Capital-First risk engine —
            all before your MT5 terminal fires an order.
          </p>
          <p className="text-[#6b7280] italic max-w-md text-sm mt-4 leading-relaxed border-l-2 border-[#3b82f6]/40 pl-3">
            &ldquo;We don&apos;t optimize for winning trades. We optimize for surviving markets.&rdquo;
          </p>
          <div className="mt-8 flex items-center gap-3 text-xs font-mono text-[#6b7280] flex-wrap">
            <span className="inline-flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#10b981] vx-dot-online" /> Market Intelligence</span>
            <span>·</span>
            <span>Risk-First AI</span>
            <span>·</span>
            <span>Health Score</span>
            <span>·</span>
            <span>AI Coach</span>
          </div>
        </div>
        <div className="text-[10px] text-[#6b7280] font-mono">© 2026 Veerox Labs</div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm space-y-5">
          <div>
            <div className="vx-label mb-2">Login</div>
            <h2 className="font-heading text-3xl font-black tracking-tighter">Access terminal</h2>
            <p className="text-sm text-[#9ca3af] mt-2">
              Don&apos;t have an account?{" "}
              <Link to="/register" className="text-[#3b82f6] hover:underline" data-testid="link-register">
                Register
              </Link>
            </p>
          </div>

          {/* Tab switcher */}
          <div className="flex rounded-md border border-[#1f2937] p-1 bg-[#0a0e1a]" data-testid="login-tabs">
            <button
              type="button"
              onClick={() => setMode("password")}
              className={`flex-1 py-1.5 text-xs font-mono uppercase tracking-widest rounded transition-colors ${
                mode === "password"
                  ? "bg-[#3b82f6]/15 text-[#3b82f6]"
                  : "text-[#6b7280] hover:text-[#9ca3af]"
              }`}
              data-testid="tab-password"
            >
              Password
            </button>
            <button
              type="button"
              onClick={() => setMode("otp")}
              className={`flex-1 py-1.5 text-xs font-mono uppercase tracking-widest rounded transition-colors ${
                mode === "otp"
                  ? "bg-[#3b82f6]/15 text-[#3b82f6]"
                  : "text-[#6b7280] hover:text-[#9ca3af]"
              }`}
              data-testid="tab-otp"
            >
              Email OTP
            </button>
          </div>

          {mode === "password" ? (
            <form onSubmit={submit} className="space-y-4" data-testid="login-form">
              {unverified && (
                <div
                  className="rounded border border-[#fbbf24]/40 bg-[#fbbf24]/10 p-3 space-y-2"
                  data-testid="unverified-banner"
                >
                  <div className="text-xs text-[#fbbf24] font-mono">
                    ⚠ Your email <span className="text-[#e5e7eb]">{unverified.email}</span> isn&apos;t verified yet.
                  </div>
                  <button
                    type="button"
                    onClick={handleUnverifiedResend}
                    disabled={resendingUnverified}
                    data-testid="resend-verification-from-login-btn"
                    className="w-full h-8 rounded border border-[#fbbf24]/40 hover:bg-[#fbbf24]/20 text-[11px] font-mono uppercase tracking-widest text-[#fbbf24] disabled:opacity-50"
                  >
                    {resendingUnverified ? "Sending…" : "Resend verification email"}
                  </button>
                </div>
              )}
              <div>
                <label className="vx-label block mb-1.5">Email</label>
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                       className="vx-input" data-testid="login-email-input" autoComplete="email" />
              </div>
              <div>
                <label className="vx-label block mb-1.5">Password</label>
                <input type="password" required minLength={8} value={password}
                       onChange={(e) => setPassword(e.target.value)} className="vx-input"
                       data-testid="login-password-input" autoComplete="current-password" />
              </div>
              <button type="submit" disabled={loading} data-testid="login-submit-btn"
                      className="w-full h-11 rounded-md bg-[#3b82f6] hover:bg-[#2563eb] font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60 active:scale-[0.98]">
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                Sign in
              </button>
            </form>
          ) : (
            <OtpLoginForm onSuccess={onLoginSuccess} initialEmail={email} setEmail={setEmail} />
          )}
          <div className="text-[11px] text-[#6b7280] font-mono border-t border-[#1f2937] pt-4 flex items-center justify-between flex-wrap gap-2">
            <span>New here? <a href="/register" className="text-[#3b82f6] hover:underline">Create account</a></span>
            <span className="text-[#6b7280]">Login via email OTP or password</span>
          </div>
        </div>
      </div>
      <Toaster theme="dark" position="top-right" toastOptions={{
        style: { background: "#111827", color: "#e5e7eb", border: "1px solid #1f2937" }
      }} />
      <AdminChoiceModal open={showAdminChoice} onDone={() => setShowAdminChoice(false)} />
    </div>
  );
}
